import { useEffect, useRef, useState } from "react";

/**
 * Subscribes to backend WebSocket for live tick updates.
 * Auto-reconnects on disconnect with exponential backoff.
 */
export function useLiveTick(backendUrl) {
  const [tick, setTick] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(1000);

  useEffect(() => {
    if (!backendUrl) return undefined;
    let cancelled = false;

    const buildUrl = () => {
      const u = new URL(backendUrl);
      const proto = u.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${u.host}/api/ws/live`;
    };

    const connect = () => {
      if (cancelled) return;
      try {
        const ws = new WebSocket(buildUrl());
        wsRef.current = ws;

        ws.onopen = () => {
          setConnected(true);
          retryRef.current = 1000;
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            setTick(data);
          } catch {
            /* ignore malformed frame */
          }
        };
        ws.onclose = () => {
          setConnected(false);
          if (!cancelled) {
            const delay = Math.min(retryRef.current, 15000);
            retryRef.current = Math.min(retryRef.current * 2, 15000);
            setTimeout(connect, delay);
          }
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        };
      } catch {
        if (!cancelled) {
          setTimeout(connect, 3000);
        }
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [backendUrl]);

  return { tick, connected };
}
